import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import '../../../styles/pages/settings/settings.scss';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { useState, useEffect } from 'react';
import getCountryByDialCode from '../../../lib/helper-functions/getCountryByDialCode';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import '../../../styles/generic/normalForm.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import isNotEmptyString from '../../../lib/helper-functions/isNotEmptyString';
import { AnimatePresence } from 'framer-motion';
import PasswordValidationView from '../../forgotPassword/PasswordValidationView';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import { submitLogToBugsnag } from '../../../lib/api/log';
import { asyncHandler } from '../../../lib/api/async-handler';
import {
  applicationJSONBodyBuilder,
  applicationWWWXRLEncodedBodyBuilder,
} from '../../../lib/utils/body-builder';
import {
  APP_ENV,
  LOGIN_API,
  PASSWORD_CHANGE_ENDPOINT,
  REGISTRATION_API,
  USER_SETTINGS_API,
  WEBAUTHN_REGISTER_API,
} from '../../../lib/constants/central-api';
import { RequestInfo } from '../../../interfaces/RequestInfo';
import {
  plusSignSVG,
  fingerPrintSVG,
  questionSVG,
} from '../../../lib/allCustomSVGs';
import { setMessage } from '../../../store/reducers/appSlice';
import { updateProfile } from '../../../store/reducers/userSlice';
import { detectWebAuthnType } from '../../webAuthn/detect-webAuthn-type';
import HELP_LINKS from '../../../lib/constants/helpLinks';

declare const window: any;

const ProfileSettings = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const accessToken = useSelector(
    (state: RootState) => state.auth?.authorization?.accessToken
  );
  const sellerToken = accessToken?.sellerToken;
  const IPCountry = useSelector(
    (state: RootState) => state.app.countries.countryOfIP
  );
  const countries = useSelector(
    (state: RootState) => state.app.countries.available
  );
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const seller_id = useSelector(
    (state: RootState) => state.user.profile.seller_id
  );
  const merchantSlug = useSelector(
    (state: RootState) => state.user.merchant.merchant_slug
  );

  const [firstName, setFirstName] = useState(userProfile?.first_name || '');
  const [lastName, setLastName] = useState(userProfile?.last_name || '');
  const [email, setEmail] = useState(userProfile?.email || '');
  const [phoneNumber, setPhoneNumber] = useState({
    number: userProfile.phone,
    country: userProfile.country_code
      ? getCountryByDialCode(userProfile.country_code)
      : countries[IPCountry],
  });

  const [isSubmittingProfile, setIsSubmittingProfile] =
    useState<boolean>(false);
  const isDisabledProfile =
    !isNotEmptyString(firstName) || !isNotEmptyString(email) ? true : false;

  /* ------------------Password Stuff Starts--------------------- */
  const [currentPassword, setCurrentPassword] = useState<string>('');
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);

  const [hasUppercase, setHasUppercase] = useState<boolean>(false);
  const [hasLowercase, setHasLowercase] = useState<boolean>(false);
  const [hasNumber, setHasNumber] = useState<boolean>(false);
  const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
  const [isLongEnough, setIsLongEnough] = useState<boolean>(false);

  const [isSubmittingPassword, setIsSubmittingPassword] =
    useState<boolean>(false);
  const isDisabledPassword =
    !hasUppercase ||
    !hasLowercase ||
    !hasNumber ||
    !hasSpecialChar ||
    !isLongEnough ||
    newPassword !== confirmPassword;

  const validatePassword = (password: string) => {
    setHasUppercase(/[A-Z]/.test(password));
    setHasLowercase(/[a-z]/.test(password));
    setHasNumber(/[0-9]/.test(password));
    setHasSpecialChar(/[^a-zA-Z0-9]/.test(password));
    setIsLongEnough(password.length >= 8);
  };

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = event.target.value;
    setNewPassword(newPassword);
    validatePassword(newPassword);
  };

  /* ------------------Password Stuff Ends--------------------- */

  /* ------------------API CALLS START--------------------- */
  async function submitProfile(e: any) {
    e.preventDefault();
    setIsSubmittingProfile(true);

    const sendingData = {
      first_name: isNotEmptyString(firstName) ? firstName : '',
      last_name: isNotEmptyString(lastName) ? lastName : firstName[0],
      // contact: phoneNumber.number,
      // country_code: phoneNumber.country?.dialCode?.substring(1),
      // email: email,
      language: userProfile?.language,
    };

    try {
      const requestInfo: RequestInfo = {
        url: `${REGISTRATION_API(merchantSlug)}/${seller_id}`,
        method: 'PUT',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: `${accessToken.type} ${sellerToken}`,
        },
      };

      const actionType = 'PROFILE_INFO_UPDATE';
      const customMsg = `${t('ProfileInfoUpdatedSuccessfully')}`;

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        true,
        false
      );

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        const { user } = response?.data;
        if (user?.seller_id) {
          dispatch(
            updateProfile({
              first_name: user?.first_name,
              last_name: user?.last_name,
              country_code: user?.country_code,
              phone: user?.contact,
              language: user?.language,
              email: user?.email,
            })
          );
          setIsSubmittingProfile(false);
        }
      } else {
        setIsSubmittingProfile(false);
      }
    } catch (exception) {
      setIsSubmittingProfile(false);
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  async function submitPasswordChange(e: any) {
    e.preventDefault();
    setIsSubmittingPassword(true);

    let isCurrentPasswordVerified = false;

    try {
      const requestInfo: RequestInfo = {
        url: LOGIN_API,
        method: 'POST',
        body: applicationJSONBodyBuilder({
          email: email,
          password: currentPassword,
        }),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      };

      const actionType = 'CURRENT_PASSWORD_VERIFICATION';
      const customMsg = 'Current Password Verified Successfully!';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        true
      );

      const response = res.data;

      if (res.actionType === actionType && response.success === true) {
        if (response?.data?.access_token) {
          isCurrentPasswordVerified = true;
        } else {
          isCurrentPasswordVerified = false;
        }
      } else {
        isCurrentPasswordVerified = false;
      }
    } catch (exception) {
      isCurrentPasswordVerified = false;
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }

    if (isCurrentPasswordVerified) {
      const sendingData = {
        email: email,
        password: newPassword,
        password_confirmation: confirmPassword,
        token: sellerToken,
      };
      try {
        const requestInfo: RequestInfo = {
          url: PASSWORD_CHANGE_ENDPOINT,
          method: 'POST',
          body: applicationWWWXRLEncodedBodyBuilder(sendingData),
          headers: {
            Accept: 'application/json',
            'Content-type': 'application/x-www-form-urlencoded',
            Authorization: `${accessToken.type} ${sellerToken}`,
          },
        };

        const actionType = 'PASSWORD_CHANGE';
        const customMsg = `${t('PasswordChangedSuccessfully')}`;

        const res: any = await asyncHandler(requestInfo, actionType, customMsg);

        const response = res.data;
        if (res.actionType === actionType && response.success === true) {
          setIsSubmittingPassword(false);
        }
      } catch (exception) {
        setIsSubmittingPassword(false);
        console.log('Error during authentication request');
      }
    } else {
      setIsSubmittingPassword(false);
      dispatch(
        setMessage({
          message: t('InvalidCurrentPassword'),
          messageType: 'error',
        })
      );
    }
  }
  async function getHideWebAuthnPromptState() {
    setSubmittingHideWebAuthnPromptChange(true);
    try {
      const requestInfo: RequestInfo = {
        url: USER_SETTINGS_API,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
          Authorization: accessToken.type + ' ' + sellerToken,
        },
      };

      const actionType = 'GET_USER_SETTINGS';
      const customMsg = 'User Settings Fetched Successfully.';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false
      );

      const response = res?.data;
      if (!response.success) {
        return false;
      }
      if (res.actionType === actionType && response.success === true) {
        if (response.data === null) {
          return false;
        } else {
          if (response?.data?.hide_webauthn_prompt === 1) {
            setHideWebAuthnPrompt(true);
          } else {
            setHideWebAuthnPrompt(false);
          }
        }
      }
      setSubmittingHideWebAuthnPromptChange(false);
    } catch (error) {
      console.error('Error fetching user settings:', error);
      setSubmittingHideWebAuthnPromptChange(false);
    }
  }
  /* ------------------API CALLS END--------------------- */

  /* ------------------Passkey Stuff Starts--------------------- */
  const webAuthnType = detectWebAuthnType();
  const [
    submittingHideWebAuthnPromptChange,
    setSubmittingHideWebAuthnPromptChange,
  ] = useState<boolean>(false);

  const [verifyingWebAuthn, setVerifyingWebAuthn] = useState<boolean>(false);
  const [hideWebAuthnPrompt, setHideWebAuthnPrompt] = useState<boolean>(true);

  useEffect(() => {
    getHideWebAuthnPromptState();
  }, []);

  async function submitHideWebAuthnPromptChange(flag: boolean) {
    setSubmittingHideWebAuthnPromptChange(true);

    const sendingData = {
      settings: {
        hide_webauthn_prompt: flag ? 1 : 2,
      },
    };

    try {
      const requestInfo: RequestInfo = {
        url: USER_SETTINGS_API,
        method: 'PUT',
        body: applicationJSONBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `${accessToken.type} ${sellerToken}`,
        },
      };

      const actionType = 'UPDATE_USER_SETTINGS';
      const customMsg = flag
        ? `${t('PasskeyRegReminderDisabled')}`
        : `${t('PasskeyRegReminderEnabled')}`;

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        true,
        false
      );

      const response = res.data;
      if (res.actionType === actionType && response.success === true) {
        setSubmittingHideWebAuthnPromptChange(false);
      } else {
        setSubmittingHideWebAuthnPromptChange(false);
      }
    } catch (exception) {
      setSubmittingHideWebAuthnPromptChange(false);
      console.log('Error during authentication request');
      submitLogToBugsnag('error', `${exception}`);
    }
  }

  async function triggerWebAuthn() {
    setVerifyingWebAuthn(true);

    const registerUUID = uuidv4();

    const attestOptionsConfig = {
      path: `${WEBAUTHN_REGISTER_API}/options`,
      headers: {
        Authorization: `${accessToken.type} ${sellerToken}`,
      },
      body: {
        phone_login: false,
        key: registerUUID,
      },
    };

    const attestConfig = {
      path: WEBAUTHN_REGISTER_API,
      headers: {
        Authorization: `${accessToken.type} ${sellerToken}`,
      },
      body: {
        key: registerUUID,
      },
    };

    try {
      console.log('WebAuthn : Registering...');

      const webpass = window.Webpass.create({
        rpId: APP_ENV === 'LIVE' ? 'adup.io' : 'kodeia.com',
      });

      const { success }: any = await webpass.attest(
        attestOptionsConfig,
        attestConfig
      );

      if (success) {
        console.log('WebAuthn : Registering successful');

        dispatch(
          setMessage({
            message: t('WebAuthnSuccess', {
              webAuthnType,
            }),
            messageType: 'success',
          })
        );
      } else {
        console.log('WebAuthn : Registering failed.');

        dispatch(
          setMessage({
            message: t('WebAuthnError', {
              webAuthnType,
            }),
            messageType: 'error',
          })
        );
      }
    } catch (error) {
      console.log('WebAuthn : Registering failed.', error);
      submitLogToBugsnag(
        'error',
        `Error in webauthn register endpoint: ${error}`
      );
      dispatch(
        setMessage({
          message: t('WebAuthnError', {
            webAuthnType,
          }),
          messageType: 'error',
        })
      );
    }

    setVerifyingWebAuthn(false);
  }

  /* ------------------Passkey Stuff Ends--------------------- */

  return (
    <SubPageInnerContentHolder
      title={t('ProfileSettings')}
      // subTitle={t('ManageProfileSettings')}
      backBtnLink={HOME_ROUTE}
      /* ------Help Button------ */
      mainButtonType='white'
      mainBtnTitle={t('Help')}
      mainBtnIcon={questionSVG}
      mainBtnLoading={false}
      mainBtnLink={HELP_LINKS.settings.profile}
      isMainBtnLinkExternal={true}
    >
      <div className='settings_mobile-only-top-margin'></div>
      <SettingsCommonCard
        mainTitle={t('ProfileInformation')}
        subTitle={t('UpdateYourPersonalInformationHere')}
      >
        <form
          id='profile-information-settings-form'
          className='normalForm'
          onSubmit={submitProfile}
        >
          <div className='normalForm__top'>
            <div className='normalForm__container1'>
              <div className='normalForm__container1--left'>
                <div className='quickOnboardingCompany__input'>
                  <AINormalInput
                    changeListeners={[
                      (e) => {
                        setFirstName(e.target.value);
                      },
                    ]}
                    id='profileSettings-first-name-input'
                    value={firstName}
                    placeholder={t('FirstName')}
                    pattern='^.{1,30}$'
                    required={true}
                    title={t('Name-validation')}
                    innerPlaceholder={t('EnterYourFirstName')}
                    minlength={1}
                    maxlength={30}
                  />
                </div>
              </div>
              <div className='normalForm__container1--right'>
                <div className='quickOnboardingCompany__input'>
                  <AINormalInput
                    changeListeners={[
                      (e) => {
                        setLastName(e.target.value);
                      },
                    ]}
                    id='profileSettings-last-name-input'
                    value={lastName}
                    placeholder={t('LastName')}
                    pattern='^.{1,30}$'
                    required={true}
                    title={t('Name-validation')}
                    innerPlaceholder={t('EnterYourLastName')}
                    minlength={1}
                    maxlength={30}
                  />
                </div>
              </div>
            </div>
            {/* <div>
              <AINormalInput
                changeListeners={[
                  (e) => {
                    setEmail(e.target.value);
                  },
                ]}
                id='sign-up-form-email'
                value={email}
                placeholder={t('EmailAddress')}
                pattern='^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$'
                required={false}
                readonly={true}
                disabled={true}
                // title={t('Email-validation')}
                // innerPlaceholder={t('EnterYourEmailAddress')}
                type='email'
              />
            </div> */}
            {/* <div>
              <AIPhoneInput
                id='profileSettings-phone-input'
                setPhoneNumber={setPhoneNumber}
                phoneNumber={phoneNumber}
                label={t('PhoneNumber')}
                placeholder={t('EnterPhoneNumber')}
                title={t('Phone-validation')}
                required={true}
                pattern='^[0-9]{4,}$'
              />
            </div> */}
          </div>
          <div
            className='normalForm__bottom flex-start'
            style={{ marginTop: '2rem' }}
          >
            <MainSquareButton
              value={
                isSubmittingProfile
                  ? `${t('Updating')}...`
                  : t('UpdateProfileBtn')
              }
              disabled={isDisabledProfile || isSubmittingProfile}
              type='submit'
              form='profile-information-settings-form'
            />
          </div>
        </form>
      </SettingsCommonCard>

      <SettingsCommonCard
        mainTitle={t('ChangePassword')}
        subTitle={t('UpdateYourPasswordHere')}
        styles={{ marginTop: '3rem' }}
      >
        <form
          id='profile-password-settings-form'
          className='normalForm'
          onSubmit={submitPasswordChange}
        >
          <div className='normalForm__top'>
            <div>
              <AINormalInput
                changeListeners={[
                  (e) => {
                    setCurrentPassword(e.target.value);
                  },
                ]}
                id='profileSettings-current-password-input'
                value={currentPassword}
                placeholder={t('CurrentPassword')}
                required={true}
                innerPlaceholder={t('EnterCurrentPassword')}
                type='password'
              />
            </div>
            <div>
              <AINormalInput
                id='profileSettings-new-password-input'
                value={newPassword}
                type='password'
                changeListeners={[(e) => handlePasswordChange(e)]}
                placeholder={t('NewPassword')}
                innerPlaceholder={t('EnterYourNewPassword')}
                required={true}
                focusListeners={[
                  (e) => {
                    setIsPasswordInputFocused(true);
                  },
                ]}
                blurListeners={[
                  (e) => {
                    setIsPasswordInputFocused(false);
                  },
                ]}
              />
              <AnimatePresence exitBeforeEnter>
                {isPasswordInputFocused && (
                  <div className='profileSettings-passwordValidation'>
                    {PasswordValidationView(
                      hasUppercase,
                      hasLowercase,
                      hasNumber,
                      hasSpecialChar,
                      isLongEnough
                    )}
                  </div>
                )}
              </AnimatePresence>
            </div>
            <div>
              <AINormalInput
                id='profileSettings-new-confirm-password-input'
                value={confirmPassword}
                type='password'
                changeListeners={[(e) => setConfirmPassword(e.target.value)]}
                focusListeners={[]}
                placeholder={t('ConfirmPassword')}
                innerPlaceholder={t('ConfirmYourNewPassword')}
                required={true}
              />
            </div>
          </div>
          <div
            className='normalForm__bottom flex-start'
            style={{ marginTop: '2rem' }}
          >
            <MainSquareButton
              value={t('ChangePassword')}
              loading={isSubmittingPassword}
              disabled={isDisabledPassword || isSubmittingPassword}
              type='submit'
              form='profile-password-settings-form'
            />
          </div>
        </form>
      </SettingsCommonCard>
      <SettingsCommonCard
        mainIconSvg={fingerPrintSVG}
        mainIconBgColor='transparent'
        mainIconSvgFillColor='transparent'
        mainTitle={t('PasskeySecurity')}
        subTitle={t('EnhanceYourAccountSecurity')}
        styles={{ marginTop: '3rem' }}
        childrenStyles={{ paddingTop: '2rem' }}
      >
        <div className='itemWithRightAction-container'>
          <div className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <h3 style={{ marginBottom: '0.5rem' }}>
                {t('RegisterNewPasskey')}
              </h3>
              <p>{t('AddNewPasskeyForSecurity')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <MainSquareButton
                value={
                  !verifyingWebAuthn
                    ? `${t('AddPasskey')}`
                    : `${t('AddingPasskey')}...`
                }
                icon={plusSignSVG}
                type='button'
                disabled={verifyingWebAuthn}
                onClick={triggerWebAuthn}
              />
            </div>
          </div>
          {/* ---------------------- */}
          <div className='itemWithRightAction'>
            <div className='itemWithRightAction__left'>
              <h3 style={{ marginBottom: '0.5rem' }}>
                {t('PasskeyRegistrationReminder')}
              </h3>
              <p>{t('ShowReminderToRegisterPasskeyAfterNewLogin')}</p>
            </div>
            <div className='itemWithRightAction__right'>
              <ToggleSwitch
                isOn={!hideWebAuthnPrompt}
                onClick={() => {
                  setHideWebAuthnPrompt(!hideWebAuthnPrompt);
                  submitHideWebAuthnPromptChange(!hideWebAuthnPrompt);
                }}
                id='Passkey-Registration-Reminder-Switch'
                disabled={submittingHideWebAuthnPromptChange}
              />
            </div>
          </div>
        </div>
      </SettingsCommonCard>
    </SubPageInnerContentHolder>
  );
};

export default ProfileSettings;
